import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, PhoneCall, CheckCircle2 } from "lucide-react";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import { PageHeader, ServiceCard, SectionHeading } from "../components/sections/Shared";
import { SERVICES, PROCESS_STEPS, COMPANY } from "../data/mock";

export default function Services() {
  return (
    <>
      <Seo
        title="Courier Services — Same-Day, Medical, Interstate | 518 Courier"
        description="All 518 Courier services: same-day, medical, scheduled routes, business, documents, last-mile and interstate courier in NY's Capital Region. Call 518-954-4701."
        path="/services"
      />
      <PageHeader
        eyebrow="Our services"
        title="Every delivery, handled like"
        accent="it's the only one."
        description="From STAT medical specimens to recurring pallet routes — explore the full range of courier and logistics services we run across the Capital Region."
        image={SERVICES[3].image}
      />

      {/* SERVICES GRID */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="container-518">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.map((s) => (
              <ServiceCard key={s.id} service={s} variant="detailed" />
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-20 md:py-24 bg-white">
        <div className="container-518">
          <SectionHeading
            eyebrow="How we operate"
            title="A clean, predictable"
            accent="4-step workflow."
            description="Whether you book one delivery a month or a hundred a day, the process never changes — because predictability is what makes logistics work."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PROCESS_STEPS.map((p, i) => (
              <div
                key={i}
                className="card-lift bg-white border border-slate-200 rounded-2xl p-6 relative overflow-hidden"
              >
                <div className="absolute -top-3 -right-3 font-display font-extrabold text-[80px] text-slate-100 leading-none select-none">
                  {p.step}
                </div>
                <div className="relative">
                  <div className="text-[#f59321] font-display font-extrabold text-2xl mb-4">
                    {p.step}
                  </div>
                  <h4 className="font-display font-bold text-[#0f2847] text-lg mb-2">
                    {p.title}
                  </h4>
                  <p className="text-sm text-slate-600 leading-relaxed">{p.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW PRICING WORKS */}
      <section className="py-20 md:py-24 bg-slate-50">
        <div className="container-518">
          <SectionHeading
            eyebrow="How pricing works"
            title="Quoted before the driver"
            accent="is dispatched."
            description="Every delivery is quoted individually. Pricing depends on distance, size, weight, service level and time of day. Dispatch will confirm the price by phone or in writing before any driver moves."
          />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: "Standard same-day", text: "Routine same-day delivery within the Capital Region — typical business documents, small parcels and local pickups.", cta: "Get a quote" },
              { title: "Rush / on-demand", text: "Direct point-to-point service when timing matters. Availability depending on driver location at the time of booking.", cta: "Call dispatch" },
              { title: "Scheduled / contract", text: "Recurring daily or weekly routes for businesses. Custom pricing based on volume, distance and schedule.", cta: "Talk to us" }
            ].map((p, i) => (
              <div key={i} className="card-lift rounded-2xl p-7 flex flex-col bg-white border border-slate-200">
                <h3 className="font-display font-extrabold text-[#0f2847] text-xl mb-3">{p.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed flex-1">{p.text}</p>
                <Link to="/get-a-quote" className="mt-6">
                  <Button className="w-full bg-[#0f2847] hover:bg-[#1e3a5f] text-white rounded-full h-12 font-semibold">
                    {p.cta}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="mt-6 text-center text-xs text-slate-500">
            All pricing is confirmed by dispatch before any driver is moved. Call{" "}
            <a className="text-[#0f2847] font-semibold underline" href={`tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`}>
              {COMPANY.phone}
            </a>{" "}
            for an exact quote.
          </p>
        </div>
      </section>
      {/* CTA */}
      <section className="py-16 md:py-20 bg-[#0f2847] text-white stripe-bg">
        <div className="container-518 flex flex-col md:flex-row gap-6 items-center justify-between">
          <h3 className="font-display text-2xl md:text-3xl font-extrabold max-w-2xl">
            Not sure which service fits? Tell us what you need to move — we'll
            build the route.
          </h3>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact">
              <Button className="bg-[#f59321] hover:bg-[#e07f10] text-white rounded-full h-12 px-6 font-semibold btn-lift">
                Get a custom quote
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href={`tel:${COMPANY.phone.replace(/[^0-9]/g, "")}`}>
              <Button
                variant="outline"
                className="bg-transparent border-white/40 text-white hover:bg-white hover:text-[#0f2847] rounded-full h-12 px-6 font-semibold"
              >
                <PhoneCall className="w-4 h-4 mr-2" />
                Talk to dispatch
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
